import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { useThemeStore } from '@client/stores/themeStore';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Card, Divider, Switch, Typography } from 'antd';
import {
    AlertTriangle,
    Bell,
    Bookmark,
    Bug,
    Check,
    ExternalLink,
    Moon,
    RefreshCw,
    Settings,
    Star,
    Sun,
    Trash2,
    X,
} from 'lucide-react';

const { Title, Text, Paragraph } = Typography;

export const Route = createFileRoute('/console/$orgId/component-showcase/button-icon-test')({
    staticData: {
        breadcrumb: { title: 'Button Icon Test' },
        featureFlag: 'enable-debug-info',
    },
    component: ButtonIconTestPage,
});

function ButtonIconTestPage() {
    const { theme, setTheme } = useThemeStore();
    const { orgId } = useParams({ from: '/console/$orgId/component-showcase/button-icon-test' });
    const isDark =
        theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    const colors = ['accent', 'red', 'orange', 'gold', 'green', 'cyan', 'blue', 'purple', 'magenta', 'gray'] as const;

    return (
        <PageController route={Route}>
            <div className="mx-auto max-w-6xl p-6">
                {/* Header */}
                <div className="mb-8 flex items-start justify-between">
                    <div>
                        <Title level={2}>Button Icon Test Page</Title>
                        <Paragraph className="max-w-2xl text-muted-foreground">
                            Test page for Button.Icon component across themes. Use this to verify styling in both light
                            and dark modes.
                        </Paragraph>
                        <div className="mt-4 flex items-center gap-4">
                            <Button.Link
                                to="/console/$orgId/component-showcase/button-variants"
                                params={{ orgId }}
                                variant="secondary"
                                className="flex items-center gap-2 text-accent-500"
                            >
                                <ExternalLink size={14} />
                                <span>Button variants showcase</span>
                            </Button.Link>
                            <Button.Link
                                to="/console/$orgId/component-showcase"
                                params={{ orgId }}
                                variant="secondary"
                                className="flex items-center gap-2 text-accent-500"
                            >
                                <Bug size={14} />
                                <span>Debug info</span>
                            </Button.Link>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 rounded-lg border border-separator bg-card px-4 py-2">
                        <Sun size={16} className={!isDark ? 'text-accent-500' : 'text-muted-foreground'} />
                        <Switch
                            checked={isDark}
                            onChange={(checked) => setTheme(checked ? 'dark' : 'light')}
                            checkedChildren={<Moon size={12} />}
                            unCheckedChildren={<Sun size={12} />}
                        />
                        <Moon size={16} className={isDark ? 'text-accent-500' : 'text-muted-foreground'} />
                    </div>
                </div>

                {/* Issue Recreation - Gray Bordered ButtonIcon */}
                <Card
                    title={
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="text-orange-500" size={20} />
                            <span>Issue Test: Gray Bordered ButtonIcon in Dark Mode</span>
                        </div>
                    }
                    className="mb-6"
                >
                    <Paragraph className="mb-4 text-muted-foreground">
                        This section recreates the issue from VInputWrapper where the gray bordered cancel button (X)
                        had a white background in dark mode.
                    </Paragraph>

                    <div className="mb-6 flex items-center gap-4 rounded-lg border border-separator bg-muted p-4">
                        <Text className="text-sm">Simulated form edit state:</Text>
                        <div className="flex gap-1">
                            <Button.Icon icon={<Check />} size="small" color="accent" filled />
                            <Button.Icon icon={<X />} size="small" color="gray" bordered />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="rounded-lg bg-card p-4">
                            <Text strong className="mb-3 block">
                                On Card Background
                            </Text>
                            <div className="flex gap-2">
                                <Button.Icon icon={<Check />} color="accent" filled tooltip="Save" />
                                <Button.Icon icon={<X />} color="gray" bordered tooltip="Cancel" />
                            </div>
                        </div>
                        <div className="rounded-lg bg-muted p-4">
                            <Text strong className="mb-3 block">
                                On Muted Background
                            </Text>
                            <div className="flex gap-2">
                                <Button.Icon icon={<Check />} color="accent" filled tooltip="Save" />
                                <Button.Icon icon={<X />} color="gray" bordered tooltip="Cancel" />
                            </div>
                        </div>
                    </div>
                </Card>

                <Divider>All Bordered Variants</Divider>

                {/* All Bordered Variants */}
                <Card title="Bordered Button Icons - All Colours" className="mb-6">
                    <div className="flex flex-wrap gap-4">
                        {colors.map((color) => (
                            <div key={color} className="flex flex-col items-center gap-2">
                                <Button.Icon icon={<Star />} color={color} bordered />
                                <Text type="secondary" className="text-xs">
                                    {color}
                                </Text>
                            </div>
                        ))}
                    </div>
                </Card>

                {/* All Borderless Variants */}
                <Card title="Borderless Button Icons - All Colours" className="mb-6">
                    <div className="flex flex-wrap gap-4">
                        {colors.map((color) => (
                            <div key={color} className="flex flex-col items-center gap-2">
                                <Button.Icon icon={<Star />} color={color} />
                                <Text type="secondary" className="text-xs">
                                    {color}
                                </Text>
                            </div>
                        ))}
                    </div>
                </Card>

                {/* All Filled Variants */}
                <Card title="Filled Button Icons - All Colours" className="mb-6">
                    <div className="flex flex-wrap gap-4">
                        {colors.map((color) => (
                            <div key={color} className="flex flex-col items-center gap-2">
                                <Button.Icon icon={<Star />} color={color} filled />
                                <Text type="secondary" className="text-xs">
                                    {color}
                                </Text>
                            </div>
                        ))}
                    </div>
                </Card>

                <Divider>Specific Icons per Colour</Divider>

                {/* Real-world Usage Examples */}
                <Card title="Real-world Usage Examples" className="mb-6">
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <Text strong className="mb-3 block">
                                Common Actions
                            </Text>
                            <div className="flex flex-wrap gap-3">
                                <div className="flex flex-col items-center gap-1">
                                    <Button.Icon icon={<Check />} color="green" bordered />
                                    <Text type="secondary" className="text-xs">
                                        Confirm
                                    </Text>
                                </div>
                                <div className="flex flex-col items-center gap-1">
                                    <Button.Icon icon={<X />} color="gray" bordered />
                                    <Text type="secondary" className="text-xs">
                                        Cancel
                                    </Text>
                                </div>
                                <div className="flex flex-col items-center gap-1">
                                    <Button.Icon icon={<Trash2 />} color="red" bordered />
                                    <Text type="secondary" className="text-xs">
                                        Delete
                                    </Text>
                                </div>
                                <div className="flex flex-col items-center gap-1">
                                    <Button.Icon icon={<RefreshCw />} color="cyan" bordered />
                                    <Text type="secondary" className="text-xs">
                                        Refresh
                                    </Text>
                                </div>
                            </div>
                        </div>
                        <div>
                            <Text strong className="mb-3 block">
                                Feature Icons
                            </Text>
                            <div className="flex flex-wrap gap-3">
                                <div className="flex flex-col items-center gap-1">
                                    <Button.Icon icon={<Star />} color="gold" />
                                    <Text type="secondary" className="text-xs">
                                        Favourite
                                    </Text>
                                </div>
                                <div className="flex flex-col items-center gap-1">
                                    <Button.Icon icon={<Bell />} color="orange" />
                                    <Text type="secondary" className="text-xs">
                                        Notify
                                    </Text>
                                </div>
                                <div className="flex flex-col items-center gap-1">
                                    <Button.Icon icon={<Bookmark />} color="purple" />
                                    <Text type="secondary" className="text-xs">
                                        Bookmark
                                    </Text>
                                </div>
                                <div className="flex flex-col items-center gap-1">
                                    <Button.Icon icon={<Settings />} color="blue" />
                                    <Text type="secondary" className="text-xs">
                                        Settings
                                    </Text>
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Size Comparison */}
                <Card title="Size Comparison - Gray Bordered" className="mb-6">
                    <div className="flex items-end gap-6">
                        <div className="flex flex-col items-center gap-2">
                            <Button.Icon icon={<X />} color="gray" bordered size="small" />
                            <Text type="secondary" className="text-xs">
                                small
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-2">
                            <Button.Icon icon={<X />} color="gray" bordered size="middle" />
                            <Text type="secondary" className="text-xs">
                                middle
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-2">
                            <Button.Icon icon={<X />} color="gray" bordered size="large" />
                            <Text type="secondary" className="text-xs">
                                large
                            </Text>
                        </div>
                    </div>
                </Card>

                {/* Background Contrast Tests */}
                <Card title="Background Contrast Tests" className="mb-6">
                    <div className="space-y-4">
                        <div className="rounded-lg bg-card p-4">
                            <Text className="mb-2 block text-sm">On Card Background (--color-card)</Text>
                            <div className="flex gap-2">
                                <Button.Icon icon={<Check />} color="green" bordered />
                                <Button.Icon icon={<X />} color="gray" bordered />
                                <Button.Icon icon={<Star />} color="gold" bordered />
                            </div>
                        </div>
                        <div className="rounded-lg bg-muted p-4">
                            <Text className="mb-2 block text-sm">On Muted Background (--color-muted)</Text>
                            <div className="flex gap-2">
                                <Button.Icon icon={<Check />} color="green" bordered />
                                <Button.Icon icon={<X />} color="gray" bordered />
                                <Button.Icon icon={<Star />} color="gold" bordered />
                            </div>
                        </div>
                        <div className="rounded-lg bg-gray-800 p-4 dark:bg-gray-900">
                            <Text className="mb-2 block text-sm text-white">On Dark Background</Text>
                            <div className="flex gap-2">
                                <Button.Icon icon={<Check />} color="green" bordered />
                                <Button.Icon icon={<X />} color="gray" bordered />
                                <Button.Icon icon={<Star />} color="gold" bordered />
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Current Theme Info */}
                <Card title="Current Theme Info">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                            <Text type="secondary">Theme Setting:</Text>
                            <Text className="ml-2 font-mono">{theme}</Text>
                        </div>
                        <div>
                            <Text type="secondary">Resolved Theme:</Text>
                            <Text className="ml-2 font-mono">{isDark ? 'dark' : 'light'}</Text>
                        </div>
                        <div>
                            <Text type="secondary">data-theme attribute:</Text>
                            <Text className="ml-2 font-mono">
                                {document.documentElement.dataset.theme || 'not set'}
                            </Text>
                        </div>
                    </div>
                </Card>
            </div>
        </PageController>
    );
}
